import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles, Bike, Caravan, Sailboat, Gem } from "lucide-react";
import { SERVICE_PAGES, SERVICE_ROUTES } from "../lib/servicePages";
import { SITE } from "../lib/siteData";
import QuoteSection from "../components/site/sections/QuoteSection";

const SPECIALTY_LINES = [
  {
    slug: "motorcycle",
    icon: Bike,
    title: "Motorcycle",
    blurb: "Sport, cruiser, touring, vintage — coverage tuned to how you ride.",
    image: "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?fm=jpg&q=80&w=900&auto=format&fit=crop",
  },
  {
    slug: "rv",
    icon: Caravan,
    title: "RV & Motorhome",
    blurb: "Class A, B, C, fifth-wheel, travel trailer — full-time or weekend.",
    image: "https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?fm=jpg&q=80&w=900&auto=format&fit=crop",
  },
  {
    slug: "watercraft",
    icon: Sailboat,
    title: "Watercraft & Boat",
    blurb: "Pontoons, bass boats, sail, ski, yacht — built for time on the water.",
    image: "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?fm=jpg&q=80&w=900&auto=format&fit=crop",
  },
  {
    slug: "jewelry",
    icon: Gem,
    title: "Jewelry & Fine Art",
    blurb: "Scheduled coverage for rings, watches, art, antiques, wine, collectibles.",
    image: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?fm=jpg&q=80&w=900&auto=format&fit=crop",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function ServiceDetail({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const data = SERVICE_PAGES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <div data-testid={`service-detail-${slug}`}>
      {/* Banner */}
      <section className="relative h-[260px] sm:h-[340px] overflow-hidden bg-[#011233]">
        {data.banner && (
          <img
            src={data.banner}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-[#011233]/72" />
        <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-8 flex flex-col justify-end pb-12">
          <motion.div {...fadeUp}>
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#B5BAC2]">
              {data.eyebrow}
            </p>
            <h1 className="mt-3 text-white text-4xl sm:text-6xl font-light tracking-tight uppercase">
              {data.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Specialty sub-lines — placed right under banner on the parent Specialty page */}
      {slug === "specialty" && (
        <section className="py-20 sm:py-28 bg-[#E5E5EA]" data-testid="specialty-lines">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <motion.div {...fadeUp} className="max-w-3xl mb-12">
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#011233] font-semibold">
                Specialty coverage lines
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-light tracking-tight text-slate-900 leading-tight">
                Explore each line in detail.
              </h2>
              <p className="mt-4 text-base text-slate-600">
                Tap a line to see the policies, endorsements, and discounts we
                routinely arrange for that coverage.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SPECIALTY_LINES.map((line, i) => {
                const Icon = line.icon;
                return (
                  <motion.div
                    key={line.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={SERVICE_ROUTES[line.slug]}
                      className="group relative block rounded-[24px] overflow-hidden bg-white ring-1 ring-[#011233]/10 transform-gpu transition-all duration-400 ease-out hover:-translate-y-4 hover:scale-[1.04] hover:ring-2 hover:ring-[#011233] hover:shadow-2xl hover:shadow-[#011233]/55"
                      data-testid={`specialty-line-${line.slug}`}
                    >
                      <div className="aspect-[4/3] overflow-hidden relative">
                        <img
                          src={line.image}
                          alt={line.title}
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-125"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#011233]/0 to-transparent group-hover:from-[#011233]/40 transition-all duration-500" />
                      </div>
                      <div className="p-6">
                        <span className="inline-flex w-11 h-11 rounded-xl bg-[#011233]/5 text-[#011233] items-center justify-center transition-all duration-300 group-hover:bg-[#011233] group-hover:text-white group-hover:scale-110 group-hover:rotate-6">
                          <Icon size={20} strokeWidth={2.2} />
                        </span>
                        <h3 className="mt-4 font-heading font-bold text-xl text-[#011233] tracking-tight group-hover:text-[#011233] transition-colors">
                          {line.title}
                        </h3>
                        <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">
                          {line.blurb}
                        </p>
                        <span className="mt-5 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#011233] font-semibold opacity-70 group-hover:opacity-100 transition-opacity">
                          Explore
                          <ArrowRight
                            size={14}
                            className="transition-transform group-hover:translate-x-1"
                          />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Intro + image */}
      <section className="py-20 sm:py-28 bg-white">
        <div className={`max-w-7xl mx-auto px-6 sm:px-8 ${data.hero_image ? "grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" : ""}`}>
          <motion.div
            {...fadeUp}
            className={data.hero_image ? "lg:col-span-6 order-2 lg:order-1" : "max-w-3xl"}
          >
            <span className="text-[11px] uppercase tracking-[0.32em] text-[#B5BAC2]">
              {SITE.brand}
            </span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-light tracking-tight text-slate-900 leading-tight">
              <span className="font-serif-italic text-[#011233]">{data.title}</span>
            </h2>
            <p className="mt-6 text-[14px] sm:text-[15px] text-slate-700 leading-relaxed">
              {data.intro}
            </p>
            <Link
              to="/#quote"
              className="kn-btn-primary inline-flex items-center gap-2 mt-8 rounded-full px-6 py-3 text-[14px] font-medium"
              data-testid={`${slug}-cta-quote`}
            >
              Get A Quote
              <ArrowRight size={16} />
            </Link>
          </motion.div>
          {data.hero_image && (
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="lg:col-span-6 order-1 lg:order-2"
            >
              <div className="rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-xl shadow-slate-900/10">
                <img
                  src={data.hero_image}
                  alt={data.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Coverage */}
      <section className="py-20 sm:py-28 bg-[#F7F9FA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div {...fadeUp} className="max-w-3xl">
            <span className="text-[11px] uppercase tracking-[0.32em] text-[#B5BAC2]">
              Coverage options
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-light tracking-tight text-slate-900 leading-tight">
              {data.coverage_heading}
            </h2>
          </motion.div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {data.coverage.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className="group flex items-center gap-4 rounded-2xl bg-white border border-black/5 px-5 py-4 cursor-pointer transform-gpu transition-all duration-300 ease-out hover:-translate-y-2 hover:scale-[1.03] hover:bg-[#011233] hover:border-transparent hover:shadow-2xl hover:shadow-[#011233]/40"
                data-testid={`coverage-${i}`}
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#011233]/5 text-[#011233] group-hover:bg-white group-hover:text-[#011233] group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 flex-shrink-0">
                  <Check size={16} />
                </span>
                <p className="text-[14px] font-medium text-slate-900 group-hover:text-white transition-colors duration-300">{c}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Discounts */}
      {data.discounts && (
        <section className="py-20 sm:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <motion.div {...fadeUp} className="max-w-3xl">
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#B5BAC2]">
                Save more
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-light tracking-tight text-slate-900 leading-tight">
                {data.discounts_heading}
              </h2>
            </motion.div>
            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {data.discounts.map((d, i) => (
                <motion.div
                  key={d}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="group relative overflow-hidden rounded-3xl bg-[#011233] text-white p-7 cursor-pointer transform-gpu transition-all duration-300 ease-out hover:-translate-y-2 hover:scale-[1.05] hover:shadow-2xl hover:shadow-[#011233]/50 hover:bg-gradient-to-br hover:from-[#0F2050] hover:to-[#011233]"
                  data-testid={`discount-${i}`}
                >
                  <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#B5BAC2]/0 group-hover:bg-[#B5BAC2]/20 transition-all duration-500" />
                  <span className="relative inline-flex w-9 h-9 rounded-full bg-[#B5BAC2]/15 text-[#B5BAC2] items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:text-[#011233] group-hover:scale-110 group-hover:rotate-12">
                    <Sparkles size={16} />
                  </span>
                  <h3 className="relative mt-5 text-base font-semibold tracking-tight transition-colors duration-300 group-hover:text-white">
                    {d}
                  </h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA — mirrors the home-page Get A Quote section */}
      <QuoteSection />
    </div>
  );
}
